import type {
  StepTranslation,
  TranslationSegment,
  FieldSegment,
  FieldType,
  TableColumn,
  TableRow,
  CheckpointDef,
} from '@/components/editor/RightPanel/TranslationPanel/translation.types';
import type { RunStageGraphEdge, ExecuteCheckpointDef, RunStageGraphVertex } from '@/types/run-stage.types';
import {
  PARAMETER_KEYS,
  EXCLUDED_PARAM_KEYS,
  NON_PARAM_KEYS,
  FIELD_TYPES,
  OPERATION_ARCHETYPES,
  MEASURE_OPERATIONS,
  NORMALIZATION_DEPTH_LIMIT,
} from './translation.constants';
import {
  extractScalar,
  extractUnits,
  extractRows,
  isParamEmpty,
  isRequired,
  toLabel,
  isFieldType,
  getSortWeight,
  detectFieldType,
} from './translation.helpers';

/**
 * Detects and normalises old mxGraph flat parameter format to standard `{ amount, units }` objects.
 *
 * OLD format (from mxGraph plan editor):
 *   edge.properties.value = { Temperature: 37, TemperatureUnits: '°C', ShakingSpeed: 150, ShakingSpeedUnits: 'rpm' }
 *
 * NEW format (from DB protocol page / backend):
 *   edge.properties.value.parameters = { Temperature: { amount: 37, units: '°C' }, ... }
 *
 * Returns normalised parameter map suitable for buildParamSegments.
 */
function normalizeFlatParams(valObj: Record<string, unknown>): Record<string, unknown> {
  try {
    // Find all *Units suffix keys and map them to their base key
    const unitsSuffixMap = new Map<string, string>();
    for (const [k, v] of Object.entries(valObj)) {
      if (k.endsWith('Units') && typeof v === 'string' && v.trim() !== '') {
        const baseKey = k.slice(0, -5);
        if (baseKey in valObj) unitsSuffixMap.set(baseKey, v);
      }
    }

    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(valObj)) {
      if (NON_PARAM_KEYS.has(k)) continue;
      if (EXCLUDED_PARAM_KEYS.has(k)) continue;
      // Skip the *Units keys — they'll be merged into the base param entry
      if (k.endsWith('Units') && unitsSuffixMap.has(k.slice(0, -5))) continue;

      if (unitsSuffixMap.has(k)) {
        result[k] = { amount: v, units: unitsSuffixMap.get(k) };
      } else {
        result[k] = v;
      }
    }
    return result;
  } catch (error) {
    console.error('Error normalizing flat params:', error);
    return {};
  }
}

/**
 * Normalize nested parameter value shape with depth limit
 */
function normalizeParamValueShape(value: unknown, depth = 0): unknown {
  if (depth > NORMALIZATION_DEPTH_LIMIT || value == null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const obj = value as Record<string, unknown>;

  if (obj.parameters && typeof obj.parameters === 'object' && !Array.isArray(obj.parameters)) {
    return normalizeParamValueShape(obj.parameters, depth + 1);
  }

  if (Object.prototype.hasOwnProperty.call(obj, 'value')) {
    const keys = Object.keys(obj);
    const allowed = new Set([
      'value', 'units', 'required', 'mandatory', 'coreField', 'fieldType',
      'label', 'displayLabel', 'parameterName', 'fieldName', 'name', 'parameterId', 'id', 'uid'
    ]);
    const extras = keys.filter((k) => !allowed.has(k));
    
    if (extras.length === 0) {
      if (typeof obj.units === 'string' && obj.units.trim()) {
        return { amount: normalizeParamValueShape(obj.value, depth + 1), units: obj.units };
      }
      return normalizeParamValueShape(obj.value, depth + 1);
    }
  }

  return value;
}

// ─── Gradient table builder ──────────────────────────────────────────────────

function buildGradientTable(
  rows: Record<string, unknown>[],
  totalPumps = 1,
  editable: boolean,
): TranslationSegment {
  const pumps = Array.from({ length: totalPumps }, (_, i) => String.fromCharCode(65 + i));
  const columns: TableColumn[] = [
    { key: 'Time', label: 'Time' },
    ...pumps.map((p) => ({ key: `Pump${p}`, label: `Pump ${p}` })),
    { key: 'Conditions', label: 'Conditions' },
  ];
  const tableRows: TableRow[] = rows.map((row) => {
    const out: TableRow = {};
    for (const col of columns) {
      out[col.key] = { value: row[col.key] ?? '', fieldType: 'text' as FieldType };
    }
    return out;
  });
  return { kind: 'table', paramName: PARAMETER_KEYS.GRADIENT_PROGRAM, columns, rows: tableRows, disabled: !editable };
}

// ─── Checkpoint builder ──────────────────────────────────────────────────────

function buildCheckpointDefs(cps: ExecuteCheckpointDef[] | undefined, editable: boolean): CheckpointDef[] {
  if (!cps?.length) return [];
  return cps.map((cp): CheckpointDef => ({
    id: cp.id ?? cp._id ?? '',
    name: cp.checkpointName ?? '',
    fieldType: (cp.fieldType ?? 'text') as CheckpointDef['fieldType'],
    mandatory: cp.required ?? false,
    disabled: !editable,
    acceptanceMin: cp.rangeMin,
    acceptanceMax: cp.rangeMax,
  }));
}

// ─── Transfer segments ───────────────────────────────────────────────────────

function buildTransferSegments(edge: RunStageGraphEdge, stepName: string, editable: boolean): TranslationSegment[] {
  const val = edge.properties?.value;
  const input = val?.input;
  const units = val?.units;
  const segs: TranslationSegment[] = [{ kind: 'text', value: 'Transfer ' }];

  segs.push({
    kind: 'field',
    paramName: PARAMETER_KEYS.INPUT,
    value: input ?? '',
    fieldType: 'number',
    disabled: !editable,
  } as FieldSegment);

  if (units) {
    segs.push({ kind: 'text', value: ` ${units} ` });
  }

  const source = String((edge.properties as any)?.sourceNodeInfo ?? (edge.properties as any)?.sourceNode ?? stepName ?? '').trim();
  const destination = String((edge.properties as any)?.destNodeInfo ?? (edge.properties as any)?.destNode ?? '').trim();
  if (destination) {
    segs.push({ kind: 'text', value: `from ${source} to ${destination}.` });
  } else {
    segs.push({ kind: 'text', value: `from ${source}.` });
  }
  return segs;
}

// ─── Parameter segments ──────────────────────────────────────────────────────

function buildParamSegments(
  parameters: Record<string, unknown>,
  hideParamsMap: Record<string, boolean | number> | undefined,
  editable: boolean,
  parameterDefinitions?: Array<{
    parameterName: string;
    fieldType: string;
    required?: boolean;
    rangeMin?: number | null;
    rangeMax?: number | null;
    parameterTags?: string[];
    dropDownList?: Array<{ id: string; label: string }>;
    tableColumns?: Array<{ id: string; header: string; required?: boolean }>;
    [key: string]: unknown;
  }>,
): { segs: TranslationSegment[]; gradientTable: TranslationSegment | null } {
  const segs: TranslationSegment[] = [];
  let gradientTable: TranslationSegment | null = null;

  // Build definition lookup by parameterName
  const defMap = new Map<string, any>(
    (parameterDefinitions || []).map((def) => [def.parameterName, def])
  );

  const defMapLower = new Map<string, any>(
    (parameterDefinitions || []).map((def) => [String(def.parameterName).toLowerCase(), def])
  );

  const keysInOrder: string[] = [];
  const seen = new Set<string>();
  [...(parameterDefinitions || [])]
    .sort((a, b) => (Number((a as any)?.order ?? 0) - Number((b as any)?.order ?? 0)))
    .forEach((d: any) => {
      const k = d?.parameterName;
      if (!k || EXCLUDED_PARAM_KEYS.has(k) || seen.has(k)) return;
      seen.add(k);
      keysInOrder.push(k);
    });
  Object.keys(parameters).forEach((k) => {
    if (EXCLUDED_PARAM_KEYS.has(k) || seen.has(k)) return;
    seen.add(k);
    keysInOrder.push(k);
  });

  if (!(parameterDefinitions || []).length) {
    keysInOrder.sort((a, b) => getSortWeight(normalizeParamValueShape(parameters[a])) - getSortWeight(normalizeParamValueShape(parameters[b])));
  }

  const visibleParams: Array<{ key: string; value: unknown }> = [];

  for (const key of keysInOrder) {
    const rawVal = normalizeParamValueShape(parameters[key]);
    const paramDef = defMap.get(key) || defMapLower.get(String(key).toLowerCase());

    const isMandatory =
      (hideParamsMap ? hideParamsMap[key] === true || hideParamsMap[key] === 1 : false) ||
      isRequired((paramDef as any)?.required) ||
      !!(paramDef as any)?.mandatory ||
      !!(paramDef as any)?.coreField;

    if (String(paramDef?.fieldType || '').toLowerCase() === 'group') {
      visibleParams.push({ key, value: rawVal });
      continue;
    }

    if (!isMandatory && isParamEmpty(rawVal)) continue;

    visibleParams.push({ key, value: rawVal });
  }

  visibleParams.forEach(({ key, value }, idx) => {
    const label = toLabel(key);
    const fieldType = detectFieldType(value);
    const paramDef = defMap.get(key) || defMapLower.get(String(key).toLowerCase());
    const rows = extractRows(value);

    if (idx > 0) segs.push({ kind: 'text', value: ', ' });
    // Group field
    if (String(paramDef?.fieldType || '').toLowerCase() === 'group' || (!paramDef && typeof value === 'object' && value !== null && !Array.isArray(value) && fieldType !== 'range' && rows.length === 0)) {
      if (label) segs.push({ kind: 'text', value: `${label}: ` });

      const groupObjRaw = value as Record<string, unknown>;
      const groupObj = (groupObjRaw.parameters && typeof groupObjRaw.parameters === 'object' && !Array.isArray(groupObjRaw.parameters))
        ? (groupObjRaw.parameters as Record<string, unknown>)
        : ((groupObjRaw.value && typeof groupObjRaw.value === 'object' && !Array.isArray(groupObjRaw.value))
          ? (groupObjRaw.value as Record<string, unknown>)
          : groupObjRaw);

      const childDefsRaw = (paramDef?.paramValues ?? paramDef?.children ?? paramDef?.subMenu ?? []) as Array<Record<string, unknown>>;
      const childDefs = Array.isArray(childDefsRaw)
        ? [...childDefsRaw].sort((a: any, b: any) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
        : [];

      if (childDefs.length) {
        const renderedChildren: TranslationSegment[] = [];

        const orderedChildDefs = [...childDefs].sort((a: any, b: any) => {
          const aType = String(a?.fieldType || '').toLowerCase();
          const bType = String(b?.fieldType || '').toLowerCase();
          const aDropdown = aType === 'dropdown' ? 0 : 1;
          const bDropdown = bType === 'dropdown' ? 0 : 1;
          if (aDropdown !== bDropdown) return aDropdown - bDropdown;
          return Number(a?.order ?? Number.MAX_SAFE_INTEGER) - Number(b?.order ?? Number.MAX_SAFE_INTEGER);
        });

        orderedChildDefs.forEach((childDef: any) => {
          const ck = String(childDef?.parameterName || childDef?.fieldName || childDef?.name || '');
          if (!ck) return;
          const cv = normalizeParamValueShape(
            groupObj[ck] ?? groupObj[ck.toLowerCase()] ?? groupObj[ck.toUpperCase()] ?? null
          );
          const mandatory = isRequired(childDef?.required) || !!childDef?.mandatory || !!childDef?.coreField;
          const cft = String(childDef?.fieldType || '').toLowerCase();
          if (!mandatory && isParamEmpty(cv)) return;

          if (renderedChildren.length > 0) renderedChildren.push({ kind: 'text', value: ', ' });
          const childLabel = String(childDef?.displayLabel || childDef?.label || toLabel(ck));
          renderedChildren.push({ kind: 'text', value: `${childLabel} ` });

          if (cft === 'dropdown' && Array.isArray(childDef?.dropDownList) && childDef.dropDownList.length > 0) {
            const dropdownVal = String(extractScalar(cv) ?? '');
            renderedChildren.push({
              kind: 'dropdown',
              paramName: `${key}.${ck}`,
              value: dropdownVal,
              options: childDef.dropDownList.map((o: any) => o?.label || o?.id || String(o)),
              disabled: !editable,
            } as any);
          } else if (detectFieldType(cv) === 'range' && typeof cv === 'object' && cv !== null) {
            const rv = cv as any;
            const left = rv.from ?? rv.minValue ?? rv.min ?? '';
            const right = rv.to ?? rv.maxValue ?? rv.max ?? '';
            renderedChildren.push({ kind: 'field', paramName: `${key}.${ck}.from`, value: left, fieldType: 'number', disabled: !editable } as FieldSegment);
            renderedChildren.push({ kind: 'text', value: ' to ' });
            renderedChildren.push({ kind: 'field', paramName: `${key}.${ck}.to`, value: right, fieldType: 'number', disabled: !editable } as FieldSegment);
          } else {
            const units = extractUnits(cv);
            const childVal = isParamEmpty(cv) ? '' : extractScalar(cv);
            renderedChildren.push({ kind: 'field', paramName: `${key}.${ck}`, value: childVal, fieldType: detectFieldType(cv), disabled: !editable, ...(units ? { unit: units } : {}) } as FieldSegment);
            if (units) renderedChildren.push({ kind: 'text', value: ` ${units}` });
          }
        });

        if (renderedChildren.length > 0) {
          segs.push(...renderedChildren);
          return;
        }
      }

      if (isParamEmpty(groupObj) && !childDefs.length) {
        return;
      }

      const fallbackEntries = Object.entries(groupObj)
        .filter(([k]) => !['parameters', 'value', 'units', 'required', 'mandatory', 'parameterId', 'parameterName', 'fieldName', 'name'].includes(k))
        .map(([k, v]) => [k, normalizeParamValueShape(v)] as const)
        .filter(([, v]) => !isParamEmpty(v));

      fallbackEntries.forEach(([ck, cv], cidx) => {
        if (cidx > 0) segs.push({ kind: 'text', value: ', ' });
        segs.push({ kind: 'text', value: `${toLabel(ck)} ` });
        const units = extractUnits(cv);
        segs.push({ kind: 'field', paramName: `${key}.${ck}`, value: extractScalar(cv), fieldType: detectFieldType(cv), disabled: !editable, ...(units ? { unit: units } : {}) } as FieldSegment);
        if (units) segs.push({ kind: 'text', value: ` ${units}` });
      });
      return;
    }
    // Dropdown field (using parameter definition)
  if (String(paramDef?.fieldType || '').toLowerCase() === 'dropdown' && paramDef?.dropDownList && paramDef.dropDownList.length > 0) {
      if (label) segs.push({ kind: 'text', value: `${label} ` });
      const options = paramDef.dropDownList.map((opt: any) => opt.label || opt.id);
      const mandatory =
        (hideParamsMap ? hideParamsMap[key] === true || hideParamsMap[key] === 1 : false) ||
        isRequired((paramDef as any)?.required) ||
        !!(paramDef as any)?.mandatory ||
        !!(paramDef as any)?.coreField;
      const dropdownVal = String(value ?? '');
      segs.push({
        kind: 'dropdown',
        paramName: key,
        value: dropdownVal,
        options,
        disabled: !editable,
      } as any); // Cast to any for now since DropdownSegment is already in TranslationSegment
      return;
    }

    // Do not infer dropdowns without explicit definition; wrapped scalar objects are common in execute payloads.

    // Range field: { from,to } or { minValue,maxValue } or { min,max }
    if (fieldType === 'range' && typeof value === 'object' && value !== null) {
      const rv = value as {
        from?: number | string;
        to?: number | string;
        minValue?: number | string;
        maxValue?: number | string;
        min?: number | string;
        max?: number | string;
        units?: string;
      };
      const left = rv.from ?? rv.minValue ?? rv.min ?? '';
      const right = rv.to ?? rv.maxValue ?? rv.max ?? '';
      if (label) segs.push({ kind: 'text', value: `${label} ` });
      segs.push({
        kind: 'field',
        paramName: `${key}.from`,
        value: left,
        fieldType: 'number',
        disabled: !editable,
      } as FieldSegment);
      segs.push({ kind: 'text', value: ' to ' });
      segs.push({
        kind: 'field',
        paramName: `${key}.to`,
        value: right,
        fieldType: 'number',
        disabled: !editable,
      } as FieldSegment);
      if (rv.units) segs.push({ kind: 'text', value: ` ${rv.units}` });
      return;
    }

    // Table/gradient field: rows from any common shape
    if (rows.length > 0) {
      // Build as inline table segment
      const firstRow = rows[0] as Record<string, unknown>;
      const columns: TableColumn[] = Object.keys(firstRow).map((colKey) => ({
        key: colKey,
        label: toLabel(colKey),
      }));
      const mappedRows: TableRow[] = rows.map((row) => {
        const out: TableRow = {};
        for (const col of columns) {
          out[col.key] = { value: row[col.key] ?? '', fieldType: 'text' as FieldType };
        }
        return out;
      });
      segs.push({ kind: 'table', paramName: key, columns, rows: mappedRows, disabled: !editable });
      return;
    }

    // Standard field
    const amount = extractScalar(value);
    const units = extractUnits(value);
    const mandatory =
      (hideParamsMap ? hideParamsMap[key] === true || hideParamsMap[key] === 1 : false) ||
      isRequired((paramDef as any)?.required) ||
      !!(paramDef as any)?.mandatory ||
      !!(paramDef as any)?.coreField;
    const displayAmount = isParamEmpty(value) ? '' : amount;

    if (label) segs.push({ kind: 'text', value: `${label} ` });

    segs.push({
      kind: 'field',
      paramName: key,
      value: displayAmount,
      fieldType: fieldType === 'units' && paramDef?.parameterTags ? 'number' : fieldType,
      disabled: !editable,
      ...(units ? { unit: units } : {}),
    } as FieldSegment);

    if (units) segs.push({ kind: 'text', value: ` ${units}` });
  });

  // Gradient program (HPLC)
  const gradientRows = parameters['GradientProgram'] as Record<string, unknown>[] | undefined;
  const totalPumps = (parameters['totalPumps'] as number | undefined) ?? 1;
  if (gradientRows && Array.isArray(gradientRows) && gradientRows.length > 0) {
    gradientTable = buildGradientTable(gradientRows, totalPumps, editable);
  }

  return { segs, gradientTable };
}

// ─── Archetype derivation ─────────────────────────────────────────────────────

function deriveArchetype(actionType?: string): 'TRANSFORM' | 'TRANSFER' | 'MEASURE' | 'COMPLEX' {
  if (!actionType) return OPERATION_ARCHETYPES.TRANSFORM;
  
  const lower = actionType.toLowerCase();
  
  if (lower === 'transfer' || lower === 'continuous') return OPERATION_ARCHETYPES.TRANSFER;
  if (MEASURE_OPERATIONS.includes(lower as any)) return OPERATION_ARCHETYPES.MEASURE;
  if (lower === 'compound') return OPERATION_ARCHETYPES.COMPLEX;
  
  return OPERATION_ARCHETYPES.TRANSFORM;
}

// ─── Main builder ─────────────────────────────────────────────────────────────

/**
 * Generate a `StepTranslation` from a raw RunStageGraphEdge.
 * This runs entirely in the frontend — no backend string is used.
 *
 * Handles three parameter storage formats:
 *  1. New DB format: `edge.properties.value.parameters = { Key: { amount, units } }`
 *  2. Old mxGraph flat format: `edge.properties.value.Key = value, edge.properties.value.KeyUnits = unit`
 *  3. Custom operation doubly-nested: `edge.properties.value.parameters.parameters = { Key: {...} }`
 *
 * @param edge             - graph edge (plan or execute)
 * @param stepNumber       - pill label ("1", "1.2", etc.)
 * @param editable         - whether parameter fields should be interactive
 * @param fallbackStepName - step name from step object, used when edge.name is absent
 */
export function buildStepTranslationFromEdge(
  edge: RunStageGraphEdge,
  stepNumber: string,
  editable: boolean,
  fallbackStepName?: string,
): StepTranslation {
  const stepName = edge.name || fallbackStepName || '';
  const actionType = edge.properties?.actionType ?? edge.subType ?? 'step';

  // ── Resolve parameters from whichever format the edge uses ──────────────
  let parameters: Record<string, unknown>;

  const rawParams = (edge.properties?.value?.parameters ?? {}) as Record<string, unknown>;

  // Custom operation: doubly-nested — value.parameters = { parameters: {...}, operationName, ... }
  if (
    rawParams &&
    typeof rawParams === 'object' &&
    !Array.isArray(rawParams) &&
    typeof (rawParams as Record<string, unknown>).parameters === 'object' &&
    (rawParams as Record<string, unknown>).parameters !== null
  ) {
    parameters = (rawParams as Record<string, unknown>).parameters as Record<string, unknown>;
  } else if (Object.keys(rawParams).length > 0) {
    // New DB format: value.parameters has entries
    parameters = rawParams;
  } else if (edge.properties?.value && typeof edge.properties.value === 'object') {
    // Old mxGraph flat format: no .parameters key — params are flat under .value
    parameters = normalizeFlatParams(edge.properties.value as Record<string, unknown>);
  } else {
    parameters = {};
  }

  const hideParamsMap = edge.properties?.hideParamsMap;
  const segments: TranslationSegment[] = [];

  if (edge.subType === 'transfer') {
    segments.push(...buildTransferSegments(edge, stepName, editable));
  } else {
    // Perform X on [stepName] at param1 v1, param2 v2.
    segments.push({ kind: 'text', value: `Perform ${actionType} on ` });
    segments.push({ kind: 'text', value: stepName });

    const { segs: paramSegs, gradientTable } = buildParamSegments(parameters, hideParamsMap, editable, undefined);

    if (paramSegs.length > 0) {
      segments.push({ kind: 'text', value: ' at ' });
      segments.push(...paramSegs);
    }

    // Duration (from edge.properties.duration or parameters.Duration/duration)
    const durValue =
      edge.properties?.duration?.value ??
      (parameters['Duration'] as { value?: number } | undefined)?.value;
    if (durValue != null) {
      segments.push({ kind: 'text', value: ' for ' });
      segments.push({
        kind: 'field',
        paramName: 'duration',
        value: durValue,
        fieldType: 'number',
        disabled: !editable,
      } as FieldSegment);
      segments.push({ kind: 'text', value: ' sec' });
    }

    segments.push({ kind: 'text', value: '.' });

    if (gradientTable) {
      segments.push(gradientTable);
    }
  }

  const checkpoints = buildCheckpointDefs(edge.checkpoints, editable);

  return {
    nodeId: `${edge.edgeId}|${edge.cloneNumber ?? 1}`,
    creationOrder: edge.creationOrder ?? 0,
    stepNumber,
    type: 'operation',
    operationName: actionType,
    archetype: deriveArchetype(actionType),
    segments,
    checkpoints,
    icons: {
      hasNotes: false,
      hasApproval: false,
      hasNotifications: false,
    },
  };
}

/**
 * Build a StepTranslation from an OperationVertex (operationsInNodes / v2 maxGraph runs).
 *
 * In this format:
 *  - vertex.parameters   → parameter map directly (no .value.parameters nesting)
 *  - vertex.checkPoints  → checkpoints
 *  - vertex.duration     → duration
 *  - vertex.operationName / vertex.name → operation / step name
 */
export function buildStepTranslationFromVertex(
  vertex: RunStageGraphVertex,
  stepNumber: string,
  editable: boolean,
  fallbackStepName?: string,
): StepTranslation {
  const stepName = vertex.name || vertex.displayName || fallbackStepName || '';
  const actionType = vertex.operationName ?? vertex.subType ?? 'step';
  // Parameters live in vertex.properties.parameters (v1 Vertex schema — Mixed bag)
  const parameters = (
    (vertex.properties?.parameters as Record<string, unknown>) ??
    vertex.parameters ??
    {}
  ) as Record<string, unknown>;
  const hideParamsMap = (vertex.properties as Record<string, unknown>)?.hideParamsMap as
    | Record<string, boolean>
    | undefined;

  const segments: TranslationSegment[] = [];

  segments.push({ kind: 'text', value: `Perform ${actionType} on ` });
  segments.push({ kind: 'text', value: stepName });

  const { segs: paramSegs, gradientTable } = buildParamSegments(parameters, hideParamsMap, editable, vertex.parameterDefinitions);

  if (paramSegs.length > 0) {
    segments.push({ kind: 'text', value: ' at ' });
    segments.push(...paramSegs);
  }

  // Duration — stored as a direct field on the v1 Vertex doc
  const durValue = vertex.duration?.value ?? (vertex.properties?.duration as { value?: number })?.value;
  if (durValue != null) {
    segments.push({ kind: 'text', value: ' for ' });
    segments.push({
      kind: 'field',
      paramName: 'duration',
      value: durValue,
      fieldType: 'number',
      disabled: !editable,
    } as FieldSegment);
    segments.push({ kind: 'text', value: ' sec' });
  }

  segments.push({ kind: 'text', value: '.' });

  if (gradientTable) segments.push(gradientTable);

  const checkpoints = buildCheckpointDefs(
    vertex.checkPoints as ExecuteCheckpointDef[] | undefined,
    editable,
  );

  return {
    nodeId: `${vertex.vertexId}|${vertex.cloneNumber ?? 1}`,
    creationOrder: vertex.creationOrder ?? 0,
    stepNumber,
    type: 'operation',
    operationName: actionType,
    archetype: deriveArchetype(actionType),
    segments,
    checkpoints,
    icons: {
      hasNotes: false,
      hasApproval: false,
      hasNotifications: false,
    },
  };
}
